import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  MediaItem,
  getMediaUrl,
  downloadMediaBlob,
  formatFileSize,
  getMediaTypeLabel,
  getProcessingStatusDisplay,
  supportsProcessing,
  updateMedia,
  UpdateMediaData,
  MEDIA_TYPE_OPTIONS,
  triggerProcessing,
  rotateImage,
  cropImage,
} from "../../api/media";
import { PersonSummary, TreeSummary, listPersons } from "../../api/trees";
import { tagMediaToPerson, tagMediaToConnectedPerson, deleteMediaTag } from "../../api/media";
import { invalidateMediaCache } from "../../hooks/useMediaUrl";
import FaceTagger from "./FaceTagger";
import { MediaImage } from "./MediaImage";
import { PersonPicker } from "../Person";

// =============================================================================
// Types
// =============================================================================

interface BaseModalProps {
  media: MediaItem;
  onClose: () => void;
  onDelete: () => void;
  onProcess: () => void;
  onMediaUpdated?: () => void;
}

interface GlobalModalProps extends BaseModalProps {
  mode: "global";
  trees: TreeSummary[];
}

interface TreeModalProps extends BaseModalProps {
  mode: "tree";
  treeId: number;
  persons: PersonSummary[];
}

export type MediaDetailModalProps = GlobalModalProps | TreeModalProps;

// =============================================================================
// Component
// =============================================================================

export function MediaDetailModal(props: MediaDetailModalProps) {
  const { media, onClose, onDelete, onProcess, onMediaUpdated } = props;
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // UI State
  const [showTranscription, setShowTranscription] = useState(false);
  const [showOcr, setShowOcr] = useState(!!media.ocr_text); // Auto-expand if OCR text exists
  const [showFaceTagger, setShowFaceTagger] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showSimplePersonTagger, setShowSimplePersonTagger] = useState(false);

  // Person loading state (for global mode)
  const [allPersons, setAllPersons] = useState<PersonSummary[]>([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  // Edit form state
  const [editCaption, setEditCaption] = useState(media.caption || "");
  const [editDescription, setEditDescription] = useState(media.description || "");
  const [editLocation, setEditLocation] = useState(media.location || "");
  const [editEstimatedDate, setEditEstimatedDate] = useState(media.estimated_date_text || "");
  const [editMediaType, setEditMediaType] = useState(media.media_type);
  const [editTranscription, _setEditTranscription] = useState(media.transcription || "");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processSuccess, setProcessSuccess] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  // Image editing state (rotation & cropping)
  const [isRotating, setIsRotating] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [imageKey, setImageKey] = useState(0); // Force image reload after edit
  const cropImageRef = useRef<HTMLImageElement>(null);
  
  // Blob URL for crop mode (needs auth)
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropImageLoading, setCropImageLoading] = useState(false);

  // Auto-expand OCR section when new text is extracted
  useEffect(() => {
    if (media.ocr_text) {
      setShowOcr(true);
    }
  }, [media.ocr_text]);

  // Cleanup crop blob URL on unmount
  useEffect(() => {
    return () => {
      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
      }
    };
  }, [cropImageUrl]);

  // PDF blob URL for authenticated viewing
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfBlobUrlRef = useRef<string | null>(null);

  const status = getProcessingStatusDisplay(media.processing_status);

  // Fetch PDF with authentication and create blob URL
  useEffect(() => {
    if (media.media_type !== "document" || media.content_type !== "application/pdf") {
      return;
    }

    const fetchPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(getMediaUrl(media.id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to load PDF");
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = url;
        setPdfBlobUrl(url);
      } catch (err) {
        setPdfError(err instanceof Error ? err.message : "Failed to load PDF");
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdf();

    // Cleanup blob URL on unmount
    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [media.id, media.media_type, media.content_type, getAccessTokenSilently]);

  // Determine persons to pass to FaceTagger
  const personsForTagger = props.mode === "global" ? allPersons : props.persons;

  // Load all persons from all trees when FaceTagger opens (global mode only)
  useEffect(() => {
    // Only run in global mode
    if (props.mode !== "global") {
      return;
    }

    const trees = props.trees;
    if (!showFaceTagger || trees.length === 0 || allPersons.length > 0 || loadingPersons) {
      return;
    }

    const loadAllPersons = async () => {
      setLoadingPersons(true);
      try {
        const token = await getAccessTokenSilently();
        const personPromises = trees.map(tree =>
          listPersons(tree.id, token, { limit: 500 })
            .then(persons =>
              persons.map(p => ({ ...p, tree_id: tree.id, tree_name: tree.name }))
            )
            .catch(() => [])
        );
        const personsArrays = await Promise.all(personPromises);
        const combined: PersonSummary[] = personsArrays.flat();
        combined.sort((a, b) => {
          const nameA = a.full_name || `${a.given_name || ""} ${a.surname || ""}`;
          const nameB = b.full_name || `${b.given_name || ""} ${b.surname || ""}`;
          return nameA.localeCompare(nameB);
        });
        setAllPersons(combined);
      } catch (err) {
        console.error("Failed to load persons:", err);
      } finally {
        setLoadingPersons(false);
      }
    };

    loadAllPersons();
  }, [props, showFaceTagger, allPersons.length, loadingPersons, getAccessTokenSilently]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateMediaData) => {
      const token = await getAccessTokenSilently();
      return updateMedia(media.id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      onMediaUpdated?.();
    },
  });

  // Person tagging mutations
  const tagPersonMutation = useMutation({
    mutationFn: async (personId: number) => {
      const token = await getAccessTokenSilently();
      return tagMediaToPerson(media.id, personId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      onMediaUpdated?.();
    },
  });

  const tagConnectedPersonMutation = useMutation({
    mutationFn: async (connectedPersonId: number) => {
      const token = await getAccessTokenSilently();
      return tagMediaToConnectedPerson(media.id, connectedPersonId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      onMediaUpdated?.();
    },
  });

  const untagPersonMutation = useMutation({
    mutationFn: async (tagId: number) => {
      const token = await getAccessTokenSilently();
      return deleteMediaTag(media.id, tagId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      onMediaUpdated?.();
    },
  });

  // Check if form has changes
  const hasChanges =
    editCaption !== (media.caption || "") ||
    editDescription !== (media.description || "") ||
    editLocation !== (media.location || "") ||
    editEstimatedDate !== (media.estimated_date_text || "") ||
    editMediaType !== media.media_type ||
    editTranscription !== (media.transcription || "");

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      caption: editCaption || undefined,
      description: editDescription || undefined,
      location: editLocation || undefined,
      estimated_date_text: editEstimatedDate || undefined,
      media_type: editMediaType !== media.media_type ? editMediaType : undefined,
      transcription: editTranscription || undefined,
    });
  }, [editCaption, editDescription, editLocation, editEstimatedDate, editMediaType, editTranscription, media.media_type, updateMutation]);

  // Handle image rotation
  const handleRotate = useCallback(async (degrees: 90 | 180 | 270) => {
    setIsRotating(true);
    setEditError(null);
    setEditSuccess(null);
    try {
      const token = await getAccessTokenSilently();
      await rotateImage(media.id, degrees, token);
      // Invalidate the blob URL cache so the image is refetched
      invalidateMediaCache(media.id);
      // Force image reload and refresh data
      setImageKey(prev => prev + 1);
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      onMediaUpdated?.();
      // Show success message briefly
      setEditSuccess("Image rotated and saved");
      setTimeout(() => setEditSuccess(null), 2000);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to rotate image");
    } finally {
      setIsRotating(false);
    }
  }, [getAccessTokenSilently, media.id, queryClient, onMediaUpdated]);

  // Handle crop mode toggle
  const handleCropModeToggle = useCallback(async () => {
    if (cropMode) {
      // Exit crop mode - cleanup blob URL
      setCropMode(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
      }
    } else {
      // Enter crop mode - load image with authentication
      setCropMode(true);
      setEditError(null);
      setCropImageLoading(true);
      
      try {
        const token = await getAccessTokenSilently();
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
        const response = await fetch(`${API_URL}/media/${media.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) {
          throw new Error("Failed to load image for cropping");
        }
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setCropImageUrl(url);
      } catch (err) {
        setEditError(err instanceof Error ? err.message : "Failed to load image");
        setCropMode(false);
      } finally {
        setCropImageLoading(false);
      }
    }
  }, [cropMode, cropImageUrl, getAccessTokenSilently, media.id]);

  // Initialize crop when image loads in crop mode
  const onCropImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
    setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
    
    // Set a centered initial crop at 80% of the image
    const initialCrop = centerCrop(
      makeAspectCrop(
        { unit: "%", width: 80 },
        naturalWidth / naturalHeight,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initialCrop);
  }, []);

  // Apply crop
  const handleApplyCrop = useCallback(async () => {
    if (!completedCrop || !imageNaturalSize || !cropImageRef.current) {
      setEditError("Please select a crop area");
      return;
    }

    const img = cropImageRef.current;
    const scaleX = imageNaturalSize.width / img.width;
    const scaleY = imageNaturalSize.height / img.height;

    // Convert pixel crop to percentages of original image
    const cropRegion = {
      x: (completedCrop.x * scaleX / imageNaturalSize.width) * 100,
      y: (completedCrop.y * scaleY / imageNaturalSize.height) * 100,
      width: (completedCrop.width * scaleX / imageNaturalSize.width) * 100,
      height: (completedCrop.height * scaleY / imageNaturalSize.height) * 100,
    };

    setIsCropping(true);
    setEditError(null);
    try {
      const token = await getAccessTokenSilently();
      await cropImage(media.id, cropRegion, token);
      // Invalidate the blob URL cache so the image is refetched
      invalidateMediaCache(media.id);
      // Cleanup crop mode blob URL
      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
      }
      // Exit crop mode first
      setCropMode(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      // Small delay to ensure cache is cleared before re-render
      await new Promise(resolve => setTimeout(resolve, 100));
      // Force image reload and refresh data
      setImageKey(prev => prev + 1);
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      onMediaUpdated?.();
      // Show success message briefly
      setEditSuccess("Image cropped and saved");
      setTimeout(() => setEditSuccess(null), 2000);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to crop image");
    } finally {
      setIsCropping(false);
    }
  }, [completedCrop, imageNaturalSize, getAccessTokenSilently, media.id, queryClient, onMediaUpdated, cropImageUrl]);

  // Handle process with polling for completion
  const handleProcess = useCallback(async () => {
    setIsProcessing(true);
    setProcessError(null);
    setProcessSuccess(false);
    
    try {
      const token = await getAccessTokenSilently();
      await triggerProcessing(media.id, token, true);
      
      // Poll for completion (processing happens in background)
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
      let attempts = 0;
      const maxAttempts = 60; // Max 2 minutes (2s intervals)
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
        
        const statusRes = await fetch(`${API_URL}/media/${media.id}/processing-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          
          if (statusData.processing_status === "completed") {
            // Refresh to get the OCR text
            await onProcess();
            setProcessSuccess(true);
            setTimeout(() => setProcessSuccess(false), 5000);
            return;
          } else if (statusData.processing_status === "failed") {
            throw new Error(statusData.processing_error || "Processing failed");
          }
          // Still processing, continue polling
        }
      }
      
      // Timeout - refresh anyway and let user see current state
      await onProcess();
      throw new Error("Processing is taking longer than expected. Close and reopen to check status.");
      
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "Processing failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [getAccessTokenSilently, media.id, onProcess]);

  // Show face tagger modal
  if (showFaceTagger && media.media_type === "photo") {
    return (
      <FaceTagger
        media={media}
        persons={personsForTagger}
        onClose={() => {
          setShowFaceTagger(false);
          onMediaUpdated?.();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-vault-900">
            {media.caption || media.original_filename}
          </h2>
          <button
            onClick={onClose}
            className="text-vault-400 hover:text-vault-600"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Preview */}
          <div className="flex-1 bg-vault-900 p-4">
            {media.media_type === "photo" ? (
              cropMode ? (
                /* Crop Mode UI */
                <div className="relative">
                  {cropImageLoading ? (
                    <div className="flex h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center gap-2 text-white">
                        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span>Loading image for cropping...</span>
                      </div>
                    </div>
                  ) : cropImageUrl ? (
                    <>
                      <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        className="mx-auto max-h-[500px]"
                      >
                        <img
                          ref={cropImageRef}
                          key={`crop-${imageKey}`}
                          src={cropImageUrl}
                          alt={media.original_filename}
                          onLoad={onCropImageLoad}
                          className="mx-auto max-h-[500px] object-contain"
                        />
                      </ReactCrop>
                      {/* Crop controls overlay */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        <button
                          onClick={handleApplyCrop}
                          disabled={isCropping || !completedCrop}
                          className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-green-700 disabled:bg-green-400"
                        >
                          {isCropping ? "Saving..." : "Save Crop"}
                        </button>
                        <button
                          onClick={handleCropModeToggle}
                          disabled={isCropping}
                          className="rounded-lg bg-vault-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-vault-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex h-[500px] items-center justify-center text-red-400">
                      Failed to load image
                    </div>
                  )}
                </div>
              ) : (
                /* Normal image view with edit controls */
                <div className="relative">
                  <MediaImage
                    key={imageKey}
                    mediaId={media.id}
                    alt={media.original_filename}
                    className="mx-auto max-h-[500px] object-contain"
                    cacheBuster={imageKey}
                  />
                  {/* Image editing controls */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 rounded-lg px-3 py-2">
                    <button
                      onClick={() => handleRotate(270)}
                      disabled={isRotating}
                      className="rounded p-2 text-white hover:bg-white/20 disabled:opacity-50"
                      title="Rotate left (90° counter-clockwise)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRotate(90)}
                      disabled={isRotating}
                      className="rounded p-2 text-white hover:bg-white/20 disabled:opacity-50"
                      title="Rotate right (90° clockwise)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
                      </svg>
                    </button>
                    <div className="w-px bg-white/30 mx-1" />
                    <button
                      onClick={handleCropModeToggle}
                      disabled={isRotating}
                      className="rounded p-2 text-white hover:bg-white/20 disabled:opacity-50"
                      title="Crop image"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    {isRotating && (
                      <div className="flex items-center text-white text-xs ml-2">
                        <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving...
                      </div>
                    )}
                  </div>
                  {editError && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                      {editError}
                    </div>
                  )}
                  {editSuccess && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm shadow-lg flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {editSuccess}
                    </div>
                  )}
                </div>
              )
            ) : media.media_type === "audio" ? (
              <audio controls className="w-full">
                <source src={getMediaUrl(media.id)} />
              </audio>
            ) : media.media_type === "video" ? (
              <video controls className="mx-auto max-h-[500px]">
                <source src={getMediaUrl(media.id)} />
              </video>
            ) : media.media_type === "document" && media.content_type === "application/pdf" ? (
              <div className="w-full h-[500px] bg-white rounded flex items-center justify-center">
                {pdfLoading && (
                  <div className="flex flex-col items-center gap-2 text-vault-500">
                    <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Loading PDF...</span>
                  </div>
                )}
                {pdfError && (
                  <div className="text-red-500 text-center">
                    <p>Failed to load PDF</p>
                    <p className="text-sm">{pdfError}</p>
                  </div>
                )}
                {pdfBlobUrl && !pdfLoading && (
                  <iframe
                    src={pdfBlobUrl}
                    className="w-full h-full rounded"
                    title={media.original_filename}
                  />
                )}
              </div>
            ) : (
              <div className="flex h-64 flex-col items-center justify-center gap-4">
                <span className="text-6xl text-vault-400">📄</span>
                <span className="text-vault-300 text-sm">{media.original_filename}</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="w-full lg:w-80 p-6">
            {/* Metadata */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-vault-500">Type</span>
                <span className="text-vault-900">{getMediaTypeLabel(media.media_type)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-vault-500">Size</span>
                <span className="text-vault-900">{formatFileSize(media.file_size)}</span>
              </div>
              {(media.date_taken || media.estimated_date_text) && (
                <div className="flex justify-between">
                  <span className="text-vault-500">Date</span>
                  <span className="text-vault-900">{media.estimated_date_text || media.date_taken}</span>
                </div>
              )}
              {media.location && (
                <div className="flex justify-between">
                  <span className="text-vault-500">Location</span>
                  <span className="text-vault-900 text-right max-w-[150px]">{media.location}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-vault-500">Uploaded</span>
                <span className="text-vault-900">{new Date(media.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Description (if set) */}
            {media.description && !showEditForm && (
              <div className="mt-4 p-3 bg-vault-50 rounded text-sm text-vault-700">
                {media.description}
              </div>
            )}

            {/* Edit Details Section */}
            <div className="mt-6">
              <button
                onClick={() => setShowEditForm(!showEditForm)}
                className="flex w-full items-center justify-between text-sm font-medium text-vault-900"
              >
                <span>Edit Details</span>
                <span>{showEditForm ? "▲" : "▼"}</span>
              </button>

              {showEditForm && (
                <div className="mt-3 space-y-3">
                  {/* Caption */}
                  <div>
                    <label className="block text-xs font-medium text-vault-600 mb-1">
                      Caption / Title
                    </label>
                    <input
                      type="text"
                      value={editCaption}
                      onChange={(e) => setEditCaption(e.target.value)}
                      placeholder="Enter a title for this media"
                      className="w-full rounded border border-vault-200 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-vault-600 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Describe the image content, who is in it, the occasion..."
                      rows={3}
                      className="w-full rounded border border-vault-200 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-xs font-medium text-vault-600 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="Where was this taken? e.g. London, England"
                      className="w-full rounded border border-vault-200 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                    />
                  </div>

                  {/* Estimated Date */}
                  <div>
                    <label className="block text-xs font-medium text-vault-600 mb-1">
                      Estimated Date
                    </label>
                    <input
                      type="text"
                      value={editEstimatedDate}
                      onChange={(e) => setEditEstimatedDate(e.target.value)}
                      placeholder="e.g. 1920, circa 1915, May 1923"
                      className="w-full rounded border border-vault-200 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                    />
                    <p className="text-xs text-vault-400 mt-1">
                      Flexible format - enter the date as you know it
                    </p>
                  </div>

                  {/* Media Type */}
                  <div>
                    <label className="block text-xs font-medium text-vault-600 mb-1">
                      Media Type
                    </label>
                    <select
                      value={editMediaType}
                      onChange={(e) => setEditMediaType(e.target.value)}
                      className="w-full rounded border border-vault-200 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none bg-white"
                    >
                      {MEDIA_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-vault-400 mt-1">
                      {MEDIA_TYPE_OPTIONS.find(o => o.value === editMediaType)?.description}
                    </p>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || updateMutation.isPending}
                    className={`w-full rounded px-3 py-2 text-sm font-medium transition-colors ${
                      hasChanges && !updateMutation.isPending
                        ? "bg-vault-600 text-white hover:bg-vault-700"
                        : "bg-vault-100 text-vault-400 cursor-not-allowed"
                    }`}
                  >
                    {updateMutation.isPending ? "Saving..." : saveSuccess ? "Saved!" : "Save Changes"}
                  </button>

                  {updateMutation.isError && (
                    <p className="text-xs text-red-600">
                      Failed to save changes. Please try again.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-vault-900">Tagged Persons</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSimplePersonTagger(!showSimplePersonTagger)}
                    className="flex items-center gap-1.5 rounded-lg bg-vault-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-vault-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                    {showSimplePersonTagger ? "Cancel" : "Tag People"}
                  </button>
                  {media.media_type === "photo" && (
                    <button
                      onClick={() => setShowFaceTagger(true)}
                      className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-amber-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      Facial
                    </button>
                  )}
                </div>
              </div>

              {/* Person Picker */}
              {showSimplePersonTagger && (
                <div className="mt-3">
                  <PersonPicker
                    selectedPersonIds={media.tags.filter(t => t.person_id).map(t => t.person_id!)}
                    selectedConnectedPersonIds={media.tags.filter(t => t.connected_person_id).map(t => t.connected_person_id!)}
                    onAddPerson={(id) => tagPersonMutation.mutate(id)}
                    onAddConnectedPerson={(id) => tagConnectedPersonMutation.mutate(id)}
                    onRemovePerson={(id) => {
                      const tag = media.tags.find(t => t.person_id === id && !t.region_x);
                      if (tag) untagPersonMutation.mutate(tag.id);
                    }}
                    onRemoveConnectedPerson={(id) => {
                      const tag = media.tags.find(t => t.connected_person_id === id && !t.region_x);
                      if (tag) untagPersonMutation.mutate(tag.id);
                    }}
                    placeholder="Select people..."
                  />
                </div>
              )}

              {media.tags.length > 0 ? (
                <div className="mt-2 space-y-1">
                  {media.tags.map((tag) => {
                    // For tree mode with a valid person_id, link to the person
                    if (props.mode === "tree" && tag.person_id) {
                      return (
                        <Link
                          key={tag.id}
                          to={`/trees/${props.treeId}/persons/${tag.person_id}`}
                          className="block rounded bg-vault-50 px-2 py-1 text-sm text-vault-700 hover:bg-vault-100"
                        >
                          {tag.person_name || `Person #${tag.person_id}`}
                        </Link>
                      );
                    }
                    // For global mode or non-tree persons, show as badge
                    return (
                      <div
                        key={tag.id}
                        className="flex items-center gap-2 rounded bg-vault-50 px-2 py-1 text-sm text-vault-700"
                      >
                        <span className={`text-xs px-1 rounded ${
                          tag.person_type === "tree" ? "bg-green-100 text-green-700" :
                          tag.person_type === "connected" ? "bg-blue-100 text-blue-700" :
                          "bg-gray-100 text-gray-700"
                        }`}>
                          {tag.person_type === "tree" ? "Tree" :
                           tag.person_type === "connected" ? "Connected" : "Name"}
                        </span>
                        <span>{tag.person_name || tag.tag_name || "Unknown"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-2 text-sm text-vault-500">No persons tagged</p>
              )}
            </div>

            {/* Locations */}
            {media.locations && media.locations.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-vault-900">Locations</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {media.locations.map((location) => (
                    <Link
                      key={location.id}
                      to={`/locations/${location.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-sm text-emerald-700 hover:bg-emerald-200 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                      </svg>
                      {location.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Memories */}
            {media.memories && media.memories.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-vault-900">Memories</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {media.memories.map((memory) => (
                    <Link
                      key={memory.id}
                      to={`/memories/${memory.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm text-purple-700 hover:bg-purple-200 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      {memory.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Processing */}
            {supportsProcessing(media.media_type) && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-vault-900">Text Extraction</h3>
                  <span className={`rounded px-2 py-0.5 text-xs bg-${status.color}-100 text-${status.color}-700`}>
                    {isProcessing ? "Processing..." : status.label}
                  </span>
                </div>
                {media.processing_status === "skipped" && !isProcessing && (
                  <p className="mt-2 text-sm text-vault-500">
                    Extract text from this {media.media_type === "document" ? "document" : "file"} to make it searchable.
                  </p>
                )}
                {isProcessing && (
                  <p className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Extracting text... This may take a moment.
                  </p>
                )}
                {processSuccess && !media.ocr_text && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Text extracted successfully! The results will appear below shortly.
                  </p>
                )}
                {processError && (
                  <p className="mt-2 text-sm text-red-600">{processError}</p>
                )}
                {media.processing_error && !processError && (
                  <p className="mt-2 text-sm text-red-600">{media.processing_error}</p>
                )}
                <button
                  onClick={handleProcess}
                  disabled={isProcessing}
                  className={`mt-2 w-full rounded px-3 py-2 text-sm font-medium transition-colors ${
                    isProcessing
                      ? "bg-vault-300 text-vault-500 cursor-not-allowed"
                      : media.processing_status === "skipped"
                      ? "bg-vault-600 text-white hover:bg-vault-700"
                      : "bg-vault-100 text-vault-700 hover:bg-vault-200"
                  }`}
                >
                  {isProcessing ? "Extracting..." :
                   processSuccess ? "Reprocess" :
                   media.processing_status === "completed" ? "Reprocess" : 
                   media.processing_status === "skipped" ? "Extract Text" : "Process Now"}
                </button>
              </div>
            )}

            {/* Transcription */}
            {media.transcription && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setShowTranscription(!showTranscription)}
                    className="flex items-center gap-2 text-sm font-medium text-vault-900"
                  >
                    <span>Transcription (OCR)</span>
                    <span>{showTranscription ? "▲" : "▼"}</span>
                  </button>
                  <span className="text-xs text-vault-500">
                    {media.transcription.length} characters
                  </span>
                </div>
                {showTranscription && (
                  <div className="mt-2 p-3 rounded bg-vault-50 border border-vault-200">
                    <pre className="max-h-60 overflow-y-auto text-sm text-vault-800 whitespace-pre-wrap font-mono leading-relaxed">
                      {media.transcription}
                    </pre>
                    <p className="text-xs text-vault-500 mt-2">
                      Note: Transcription can be edited via the metadata edit form above.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* OCR Text */}
            {media.ocr_text && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowOcr(!showOcr)}
                    className="flex items-center gap-2 text-sm font-medium text-vault-900"
                  >
                    <span>Extracted Text (OCR)</span>
                    <span>{showOcr ? "▲" : "▼"}</span>
                  </button>
                  <Link
                    to={`/media/${media.id}/edit-text`}
                    className="text-xs text-vault-600 hover:text-vault-800 hover:underline"
                  >
                    Edit Text
                  </Link>
                </div>
                {showOcr && (
                  <div className="mt-2 max-h-40 overflow-y-auto rounded bg-vault-50 p-2 text-sm text-vault-700 whitespace-pre-wrap">
                    {media.ocr_text}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const token = await getAccessTokenSilently();
                    const blob = await downloadMediaBlob(media.id, token);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = media.original_filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error("Download failed:", err);
                    alert(err instanceof Error ? err.message : "Download failed");
                  }
                }}
                className="flex-1 rounded bg-vault-600 px-3 py-2 text-center text-sm text-white hover:bg-vault-700"
              >
                Download
              </button>
              <button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this media?")) {
                    onDelete();
                  }
                }}
                className="flex-1 rounded bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
